import { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { MultiSelectPopover } from '../components/ui/multi-select-popover';
import { Loader2, TrendingUp, UserCircle, Calendar, CheckCircle2, XCircle, RotateCcw, Users2, Search, X, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../lib/auth';
import {
  listFrontendPipeline,
  getFrontendPipelineEntry,
  updateFrontendPipelineEntry,
  winFrontendPipelineEntry,
  loseFrontendPipelineEntry,
  reopenFrontendPipelineEntry,
  deleteFrontendPipelineEntry,
  backfillFrontendPipeline,
  getFrontendPipelineProducts,
  getUsers,
} from '../lib/api';

const STAGES = [
  {
    id: 'da_assegnare',
    label: 'Da assegnare',
    header: 'bg-slate-100 text-slate-700 border-slate-300',
    accent: 'border-l-slate-400',
    dot: 'bg-slate-400',
    columnBg: 'bg-slate-50/60',
    dropRing: 'ring-slate-400',
  },
  {
    id: 'in_gestione',
    label: 'In gestione',
    header: 'bg-blue-50 text-blue-700 border-blue-200',
    accent: 'border-l-blue-500',
    dot: 'bg-blue-500',
    columnBg: 'bg-blue-50/40',
    dropRing: 'ring-blue-500',
  },
  {
    id: 'proposta',
    label: 'Proposta',
    header: 'bg-amber-50 text-amber-800 border-amber-200',
    accent: 'border-l-amber-500',
    dot: 'bg-amber-500',
    columnBg: 'bg-amber-50/40',
    dropRing: 'ring-amber-500',
  },
  {
    id: 'vinto',
    label: 'Vinto',
    header: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    accent: 'border-l-emerald-500',
    dot: 'bg-emerald-500',
    columnBg: 'bg-emerald-50/40',
    dropRing: 'ring-emerald-500',
  },
];
const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.id, s]));

const fmtCur = (v) => (v == null || v === '') ? '-' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const fmtDate = (v) => {
  if (!v) return '-';
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toLocaleDateString('it-IT');
  } catch { return String(v).slice(0, 10); }
};

export default function FrontEndPipelinePage() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const isAdminHr = hasRole(['admin', 'hr']);
  const isAccount = hasRole(['account']);
  const canWrite = isAdminHr || isAccount;  // PM = view-only
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('aperto'); // aperto | vinto | perso | all
  const [pmList, setPmList] = useState([]);
  const [amList, setAmList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [buList, setBuList] = useState([]);
  const [selectedPmIds, setSelectedPmIds] = useState([]);
  const [selectedAmIds, setSelectedAmIds] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [selectedBuIds, setSelectedBuIds] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedEntryMode, setSelectedEntryMode] = useState('edit'); // 'edit' | 'win'
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter };
      if (selectedPmIds.length > 0) params.pm_ids = selectedPmIds.join(',');
      if (selectedAmIds.length > 0) params.account_manager_ids = selectedAmIds.join(',');
      if (selectedProductIds.length > 0) params.product_ids = selectedProductIds.join(',');
      if (selectedBuIds.length > 0) params.business_unit_ids = selectedBuIds.join(',');
      const res = await listFrontendPipeline(params);
      setEntries(res.data?.entries || []);
    } catch (err) {
      toast({ title: 'Errore caricamento pipeline', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedPmIds, selectedAmIds, selectedProductIds, selectedBuIds, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdminHr) {
      getUsers('project_manager').then(res => setPmList(res.data || [])).catch(() => {});
    }
    getUsers('account').then(res => setAmList(res.data || [])).catch(() => {});
    // Ricarica anche dopo un load() (potrebbero esserci nuovi FE contratti)
    getFrontendPipelineProducts().then(res => {
      setProductList(res.data?.products || []);
      setBuList(res.data?.business_units || []);
    }).catch(() => {});
  }, [isAdminHr, entries.length]);

  const handleBackfill = async () => {
    setBackfillLoading(true);
    try {
      const res = await backfillFrontendPipeline();
      toast({ title: 'Backfill completato', description: `Scansionati ${res.data.scanned} contratti, create ${res.data.created} entry.` });
      load();
    } catch (err) {
      toast({ title: 'Errore backfill', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBackfillLoading(false);
    }
  };

  const openEntry = async (id, mode = 'edit') => {
    try {
      const res = await getFrontendPipelineEntry(id, false);
      setSelectedEntry(res.data);
      setSelectedEntryMode(mode);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const reloadEntry = async (id, includeAll) => {
    try {
      const res = await getFrontendPipelineEntry(id, includeAll);
      setSelectedEntry(res.data);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  // ── Drag & Drop ──
  const canDragEntry = (entry) => {
    // Aperti e persi si possono spostare; i vinti no (usa "Riapri" nel dialog)
    if (entry.status === 'vinto') return false;
    if (!canWrite) return false;  // PM/venditore: view-only
    if (isAdminHr) return true;
    return true;
  };

  const handleDragStart = (e, entry) => {
    if (!canDragEntry(entry)) {
      e.preventDefault();
      return;
    }
    setDraggingId(entry.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', entry.id);
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };
  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageId) setDragOverStage(stageId);
  };
  const handleDragEnter = (e, stageId) => {
    e.preventDefault();
    if (dragOverStage !== stageId) setDragOverStage(stageId);
  };
  const handleDragLeaveCol = (stageId) => {
    if (dragOverStage === stageId) setDragOverStage(null);
  };
  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const entryId = e.dataTransfer.getData('text/plain');
    if (!entryId) return;
    const entry = entries.find(x => x.id === entryId);
    if (!entry) return;
    if (entry.stage === targetStage && entry.status === 'aperto') return;

    // Card PERSA: se la spostiamo altrove va prima riaperta.
    // Se target == 'vinto' → riapri poi apri dialog Win.
    // Altrimenti → riapri con new_stage.
    if (entry.status === 'perso') {
      try {
        if (targetStage === 'vinto') {
          await reopenFrontendPipelineEntry(entry.id);
          openEntry(entry.id, 'win');
        } else {
          await reopenFrontendPipelineEntry(entry.id, targetStage);
        }
        await load();
      } catch (err) {
        toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
      }
      return;
    }

    // Card APERTA verso Vinto → dialog Win
    if (targetStage === 'vinto') {
      openEntry(entry.id, 'win');
      return;
    }
    // Altrimenti aggiorna stage direttamente (ottimistico)
    const prev = [...entries];
    setEntries(entries.map(x => x.id === entryId ? { ...x, stage: targetStage } : x));
    try {
      await updateFrontendPipelineEntry(entryId, { stage: targetStage });
      toast({ title: 'Spostato', description: `→ ${STAGE_MAP[targetStage].label}` });
    } catch (err) {
      setEntries(prev);
      toast({ title: 'Errore spostamento', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const filteredEntries = searchQuery.trim()
    ? entries.filter(e => {
        const q = searchQuery.trim().toLowerCase();
        return (e.contract_name || '').toLowerCase().includes(q)
          || (e.client_name || '').toLowerCase().includes(q)
          || (e.product_name || '').toLowerCase().includes(q);
      })
    : entries;

  const byStage = STAGES.reduce((acc, s) => {
    acc[s.id] = filteredEntries.filter((e) => e.stage === s.id);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="frontend-pipeline-title">Pipeline Front-End</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestione contratti assessment iniziali. {filteredEntries.length} entries visualizzate
            {searchQuery.trim() && entries.length !== filteredEntries.length && (
              <span className="text-gray-400"> (di {entries.length} totali)</span>
            )}.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="text"
              placeholder="Cerca cliente o scheda…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-8 pr-8 w-[220px] text-sm"
              data-testid="pipeline-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="pipeline-search-clear"
                aria-label="Cancella ricerca"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList data-testid="status-tabs">
              <TabsTrigger value="aperto" data-testid="tab-aperto">Aperti</TabsTrigger>
              <TabsTrigger value="vinto" data-testid="tab-vinto">Vinti</TabsTrigger>
              <TabsTrigger value="perso" data-testid="tab-perso">Persi</TabsTrigger>
              <TabsTrigger value="all" data-testid="tab-all">Tutti</TabsTrigger>
            </TabsList>
          </Tabs>
          {isAdminHr && (
            <MultiSelectPopover
              items={pmList.map(u => ({ id: u.id, label: u.name || u.email }))}
              selectedIds={selectedPmIds}
              onChange={setSelectedPmIds}
              placeholder="Tutti i PM"
              width="w-[180px]"
              testId="pm-filter"
            />
          )}
          <MultiSelectPopover
            items={amList.map(u => ({ id: u.id, label: u.name || u.email }))}
            selectedIds={selectedAmIds}
            onChange={setSelectedAmIds}
            placeholder="Tutti gli AM"
            width="w-[180px]"
            testId="am-filter"
          />
          <MultiSelectPopover
            items={buList.map(bu => ({ id: bu.id, label: bu.name }))}
            selectedIds={selectedBuIds}
            onChange={setSelectedBuIds}
            placeholder="Tutte le BU"
            width="w-[170px]"
            testId="bu-filter"
          />
          <MultiSelectPopover
            items={productList.map(p => ({ id: p.id, label: p.name }))}
            selectedIds={selectedProductIds}
            onChange={setSelectedProductIds}
            placeholder="Tutti i prodotti"
            width="w-[190px]"
            testId="product-filter"
          />
          {isAdminHr && (
            <Button variant="outline" onClick={handleBackfill} disabled={backfillLoading} data-testid="backfill-btn" size="sm">
              {backfillLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Sincronizza
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 border border-dashed rounded-xl bg-white">
          <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            Nessun Front-End in pipeline con questi filtri.
            {isAdminHr && ' Se hai contratti Tipologia=Front-End esistenti clicca "Sincronizza".'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline-board">
          {STAGES.map((stage) => {
            const items = byStage[stage.id] || [];
            const isDragOver = dragOverStage === stage.id;
            return (
              <div
                key={stage.id}
                onDragEnter={(e) => handleDragEnter(e, stage.id)}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={() => handleDragLeaveCol(stage.id)}
                onDrop={(e) => handleDrop(e, stage.id)}
                className={`rounded-xl border ${stage.columnBg} border-gray-200 ${isDragOver ? `ring-2 ${stage.dropRing} ring-offset-1` : ''} transition-all`}
                data-testid={`stage-column-${stage.id}`}
              >
                <div className={`flex items-center justify-between px-3 py-2 border-b border-gray-200 rounded-t-xl ${stage.header}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${stage.dot}`}></span>
                    <span className="text-sm font-semibold truncate">{stage.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="outline" className="bg-white text-gray-700 border-gray-300 h-5 min-w-5 px-1.5 text-[10px] font-semibold" data-testid={`count-${stage.id}`}>
                      {items.length}
                    </Badge>
                    <span
                      className="text-[10px] font-semibold text-gray-700 bg-white/70 border border-gray-200 rounded-md px-1.5 py-0.5 whitespace-nowrap"
                      title={`Valore totale opportunità in ${stage.label}`}
                      data-testid={`stage-total-${stage.id}`}
                    >
                      {fmtCur(items.reduce((s, e) => s + (Number(e.opportunity_value) || 0), 0))}
                    </span>
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[280px]">
                  {items.length === 0 && isDragOver && (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg h-24 flex items-center justify-center text-xs text-gray-400">
                      Rilascia qui
                    </div>
                  )}
                  {items.map((e) => {
                    const canDrag = canDragEntry(e);
                    const isDragging = draggingId === e.id;
                    const isPerso = e.status === 'perso';
                    const isVinto = e.status === 'vinto';
                    return (
                      <div
                        key={e.id}
                        draggable={canDrag}
                        onDragStart={(ev) => handleDragStart(ev, e)}
                        onDragEnd={handleDragEnd}
                        onClick={() => openEntry(e.id)}
                        data-testid={`entry-card-${e.id}`}
                        className={`
                          group relative rounded-lg border border-l-4 ${stage.accent} bg-white
                          shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer
                          ${isDragging ? 'opacity-40 rotate-1' : ''}
                          ${isPerso ? 'opacity-60' : ''}
                          ${canDrag ? 'active:cursor-grabbing' : 'cursor-pointer'}
                        `}
                      >
                        <div className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-semibold text-sm text-gray-900 line-clamp-2 leading-tight flex-1" title={e.contract_name || undefined}>
                              {(() => {
                                const client = e.client_name || 'Cliente sconosciuto';
                                const prod = e.product_name || '';
                                return prod ? `${client} - ${prod}` : client;
                              })()}
                            </div>
                            {isPerso && <Badge variant="destructive" className="text-[9px] h-4 flex-shrink-0">Perso</Badge>}
                            {isVinto && <Badge className="bg-emerald-600 text-white text-[9px] h-4 flex-shrink-0">Vinto</Badge>}
                          </div>

                          <div className="mt-2.5 flex items-center justify-between">
                            <div
                              className={`text-sm font-bold tabular-nums ${
                                Number(e.opportunity_value || 0) === 0
                                  ? 'text-red-600'
                                  : 'text-gray-900'
                              }`}
                              data-testid={`entry-value-${e.id}`}
                              title={Number(e.opportunity_value || 0) === 0 ? 'Valore non aggiornato — aggiornalo aprendo la card' : undefined}
                            >
                              {fmtCur(e.opportunity_value)}
                            </div>
                            <div className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              e.days_in_stage <= 7 ? 'bg-emerald-50 text-emerald-700'
                                : e.days_in_stage <= 30 ? 'bg-amber-50 text-amber-700'
                                : 'bg-red-50 text-red-700'
                            }`}>
                              {e.days_in_stage}gg
                            </div>
                          </div>

                          {(e.assigned_pms || []).length > 0 ? (
                            <div className="mt-2 flex items-center gap-1 flex-wrap">
                              {e.assigned_pms.slice(0, 3).map((p) => {
                                const initials = (p.name || p.email || '?').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
                                return (
                                  <div
                                    key={p.id}
                                    className="flex items-center gap-1 bg-indigo-50 text-indigo-700 rounded-full pl-0.5 pr-2 py-0.5 text-[10px] font-medium"
                                    title={p.name}
                                  >
                                    <span className="w-4 h-4 rounded-full bg-indigo-600 text-white text-[9px] font-bold flex items-center justify-center">{initials}</span>
                                    <span className="line-clamp-1 max-w-[80px]">{(p.name || p.email || '').split(' ')[0]}</span>
                                  </div>
                                );
                              })}
                              {e.assigned_pms.length > 3 && (
                                <span className="text-[10px] text-gray-500">+{e.assigned_pms.length - 3}</span>
                              )}
                            </div>
                          ) : (
                            <div className="mt-2 flex items-center gap-1 text-[10px] text-red-600 font-medium">
                              <UserCircle className="w-3 h-3" /> Da assegnare
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EntryDialog
        entry={selectedEntry}
        initialMode={selectedEntryMode}
        onClose={() => setSelectedEntry(null)}
        onSaved={() => { setSelectedEntry(null); load(); }}
        onReloadEntry={reloadEntry}
        isAdminHr={isAdminHr}
        canWrite={canWrite}
        pmList={pmList}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Entry Detail Dialog
// ═══════════════════════════════════════════════════════════════════════════
function EntryDialog({ entry, initialMode = 'edit', onClose, onSaved, onReloadEntry, isAdminHr, canWrite = true, pmList }) {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(initialMode); // edit | win | lose
  const [winData, setWinData] = useState({ outcome_date: new Date().toISOString().slice(0, 10), converted_contract_ids: [] });
  const [loseData, setLoseData] = useState({ outcome_date: new Date().toISOString().slice(0, 10), outcome_reason: '' });
  const [showAllClientsInWin, setShowAllClientsInWin] = useState(false);
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  // Edit outcome (per entries chiuse: vinto/perso)
  const [editOutcomeDate, setEditOutcomeDate] = useState('');
  const [editConvertedIds, setEditConvertedIds] = useState([]);

  const handleToggleAllClients = async (checked) => {
    setShowAllClientsInWin(Boolean(checked));
    if (entry?.id && onReloadEntry) {
      await onReloadEntry(entry.id, Boolean(checked));
    }
  };

  useEffect(() => {
    if (entry) {
      setForm({
        stage: entry.stage,
        opportunity_value: entry.opportunity_value ?? '',
        notes: entry.notes ?? '',
        assigned_pm_ids: entry.assigned_pm_ids || [],
      });
      setMode(initialMode);
      setWinData({
        outcome_date: entry.outcome_date ? String(entry.outcome_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        converted_contract_ids: entry.converted_contract_ids || [],
      });
      setLoseData({
        outcome_date: entry.outcome_date ? String(entry.outcome_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
        outcome_reason: entry.outcome_reason || '',
      });
      setShowAllClientsInWin(false);
      setEditOutcomeDate(entry.outcome_date ? String(entry.outcome_date).slice(0, 10) : '');
      setEditConvertedIds(entry.converted_contract_ids || []);
    }
    // Reset solo quando cambia l'entry (id) o si riapre; non su refetch della stessa entry
  }, [entry?.id]);

  if (!entry || !form) return null;
  const isClosed = entry.status !== 'aperto';

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        stage: form.stage,
        notes: form.notes || null,
      };
      // Sui vinti l'opportunity_value è auto-calcolato → non inviarlo
      if (entry.status !== 'vinto') {
        payload.opportunity_value = form.opportunity_value === '' ? null : Number(form.opportunity_value);
      }
      if (isAdminHr) payload.assigned_pm_ids = form.assigned_pm_ids;
      // Modifica outcome_date + converted per entries chiuse
      if (isClosed && editOutcomeDate) {
        payload.outcome_date = editOutcomeDate;
      }
      if (entry.status === 'vinto') {
        payload.converted_contract_ids = editConvertedIds;
      }
      await updateFrontendPipelineEntry(entry.id, payload);
      toast({ title: 'Entry aggiornata' });
      onSaved();
    } catch (err) {
      toast({ title: 'Errore salvataggio', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const toggleEditConverted = (cid) => {
    setEditConvertedIds(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  };

  const handleWin = async () => {
    setSaving(true);
    try {
      await winFrontendPipelineEntry(entry.id, winData);
      toast({ title: 'Front-End chiuso come Vinto', description: `Data esito: ${fmtDate(winData.outcome_date)}` });
      onSaved();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleLose = async () => {
    setSaving(true);
    try {
      await loseFrontendPipelineEntry(entry.id, loseData);
      toast({ title: 'Front-End segnato come Perso' });
      onSaved();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleReopen = async () => {
    setSaving(true);
    try {
      await reopenFrontendPipelineEntry(entry.id);
      toast({ title: 'Front-End riaperto' });
      onSaved();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const label = entry.contract_name || 'questa card';
    if (!window.confirm(`Sei sicuro di voler eliminare "${label}" dalla Pipeline Front-End?\n\nQuesta operazione rimuove SOLO la card dalla pipeline. Il contratto sottostante NON verrà cancellato.`)) {
      return;
    }
    setSaving(true);
    try {
      await deleteFrontendPipelineEntry(entry.id);
      toast({ title: 'Card eliminata dalla Pipeline' });
      onSaved();
    } catch (err) {
      toast({ title: 'Errore eliminazione', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const togglePM = (pmId) => {
    setForm(f => ({
      ...f,
      assigned_pm_ids: f.assigned_pm_ids.includes(pmId)
        ? f.assigned_pm_ids.filter(x => x !== pmId)
        : [...f.assigned_pm_ids, pmId],
    }));
  };

  const toggleConvertedContract = (cid) => {
    setWinData(d => {
      const newIds = d.converted_contract_ids.includes(cid)
        ? d.converted_contract_ids.filter(x => x !== cid)
        : [...d.converted_contract_ids, cid];
      // Auto-align outcome_date to the signing_date of selected contract(s).
      // If multiple selected, use the most recent signing_date. If none selected, keep current outcome_date.
      const selectedContracts = (entry.client_contracts || []).filter(c => newIds.includes(c.id));
      const signingDates = selectedContracts
        .map(c => c.signing_date ? String(c.signing_date).slice(0, 10) : null)
        .filter(Boolean);
      const outcome_date = signingDates.length > 0
        ? signingDates.sort().slice(-1)[0]
        : d.outcome_date;
      return { ...d, converted_contract_ids: newIds, outcome_date };
    });
  };

  const convertedTotal = (entry.client_contracts || [])
    .filter(c => winData.converted_contract_ids.includes(c.id))
    .reduce((s, c) => s + (Number(c.value) || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="entry-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span title={entry.contract_name || undefined}>
              {(() => {
                const client = entry.client_name || 'Cliente sconosciuto';
                const prod = entry.product_name || '';
                return prod ? `${client} - ${prod}` : client;
              })()}
            </span>
            {entry.status === 'perso' && <Badge variant="destructive">Perso</Badge>}
            {entry.status === 'vinto' && <Badge className="bg-emerald-600">Vinto</Badge>}
            {entry.status === 'aperto' && <Badge variant="outline">Aperto</Badge>}
          </DialogTitle>
          <DialogDescription>
            Cliente: <strong>{entry.client_name || '—'}</strong> · Valore contratto FE: {fmtCur(entry.contract_value)} · {entry.days_in_stage}gg in fase
          </DialogDescription>
        </DialogHeader>

        {/* Tabs interne edit/win/lose */}
        <div className="border-b flex gap-2 -mb-px">
          <button type="button" onClick={() => setMode('edit')} className={`px-3 py-1.5 text-sm font-medium border-b-2 ${mode === 'edit' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500'}`} data-testid="mode-edit">{canWrite ? 'Modifica' : 'Dettagli'}</button>
          {canWrite && !isClosed && (<>
            <button type="button" onClick={() => setMode('win')} className={`px-3 py-1.5 text-sm font-medium border-b-2 ${mode === 'win' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500'}`} data-testid="mode-win">
              <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Chiudi Vinto
            </button>
            <button type="button" onClick={() => setMode('lose')} className={`px-3 py-1.5 text-sm font-medium border-b-2 ${mode === 'lose' ? 'border-red-600 text-red-700' : 'border-transparent text-gray-500'}`} data-testid="mode-lose">
              <XCircle className="w-3.5 h-3.5 inline mr-1" />Segna Perso
            </button>
          </>)}
          {canWrite && isClosed && (
            <button type="button" onClick={handleReopen} className="ml-auto px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800" data-testid="mode-reopen">
              <RotateCcw className="w-3.5 h-3.5 inline mr-1" />Riapri
            </button>
          )}
          {!canWrite && (
            <span className="ml-auto px-3 py-1.5 text-xs font-medium text-gray-500 italic" data-testid="readonly-badge">Solo lettura</span>
          )}
          {isAdminHr && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className={`${isClosed || !canWrite ? '' : 'ml-auto'} px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50`}
              data-testid="mode-delete"
              title="Elimina definitivamente questa card dalla pipeline (non cancella il contratto)"
            >
              <Trash2 className="w-3.5 h-3.5 inline mr-1" />Elimina card
            </button>
          )}
        </div>

        {/* Edit mode */}
        {mode === 'edit' && (
          <div className="space-y-4 pt-2">
            <div>
              <Label>Fase</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })} disabled={!canWrite}>
                <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valore Opportunità</Label>
              <Input
                type="number"
                value={form.opportunity_value}
                onChange={(e) => setForm({ ...form, opportunity_value: e.target.value })}
                data-testid="opportunity-value-input"
                disabled={!canWrite || entry.status === 'vinto'}
                readOnly={!canWrite || entry.status === 'vinto'}
              />
              {entry.status === 'vinto' ? (
                <p className="text-[11px] text-gray-500 mt-1">
                  Calcolato automaticamente dalla somma dei contratti collegati. Non modificabile.
                </p>
              ) : (
                Number(form.opportunity_value || 0) === 0 && (
                  <p className="text-[11px] text-red-600 mt-1 font-medium">
                    Ricordati di aggiornare il valore stimato dell&apos;opportunità (attualmente 0,00 €).
                  </p>
                )
              )}
            </div>
            {isAdminHr && (
              <div>
                <Label className="flex items-center gap-1"><Users2 className="w-3.5 h-3.5" /> Project Manager assegnati</Label>
                <div className="mt-1 border border-gray-200 rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
                  {pmList.length === 0 ? <p className="text-xs text-gray-400">Nessun PM disponibile</p> : pmList.map(pm => (
                    <label key={pm.id} className="flex items-center gap-2 text-sm py-1 hover:bg-gray-50 rounded px-1 cursor-pointer">
                      <Checkbox
                        checked={form.assigned_pm_ids.includes(pm.id)}
                        onCheckedChange={() => togglePM(pm.id)}
                        data-testid={`pm-checkbox-${pm.id}`}
                      />
                      <span>{pm.name || pm.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {!isAdminHr && (entry.assigned_pms || []).length > 0 && (
              <div>
                <Label>Assegnati a</Label>
                <div className="text-sm text-gray-700 mt-1">{entry.assigned_pms.map(p => p.name).join(', ')}</div>
              </div>
            )}
            <div>
              <Label>Note</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="notes-input" disabled={!canWrite} readOnly={!canWrite} />
            </div>

            {/* Sezione Esito (per entries chiuse: vinto/perso) */}
            {isClosed && (
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <Label className="text-sm font-semibold">
                    Esito {entry.status === 'vinto' ? '(Vinto)' : '(Perso)'}
                  </Label>
                </div>
                <div>
                  <Label className="text-xs">Data esito</Label>
                  <Input
                    type="date"
                    value={editOutcomeDate}
                    onChange={(e) => setEditOutcomeDate(e.target.value)}
                    data-testid="edit-outcome-date-input"
                    disabled={!canWrite}
                    readOnly={!canWrite}
                  />
                </div>
                {entry.status === 'perso' && entry.outcome_reason && (
                  <div>
                    <Label className="text-xs">Motivo</Label>
                    <div className="text-sm text-gray-700 mt-1 p-2 bg-gray-50 rounded border">{entry.outcome_reason}</div>
                  </div>
                )}
                {entry.status === 'vinto' && (
                  <div>
                    <Label className="text-xs">Prodotti/contratti acquistati dopo la consulenza</Label>
                    <p className="text-[11px] text-gray-500 mb-1.5">
                      Contratti effettivamente venduti al cliente dopo il Front-End. Puoi aggiungere/togliere selezioni.
                    </p>
                    {/* Lista che unisce converted (già collegati) + client_contracts (disponibili nel cliente) */}
                    {(() => {
                      const alreadyById = new Map((entry.converted_contracts || []).map(c => [c.id, c]));
                      const availableById = new Map((entry.client_contracts || []).map(c => [c.id, c]));
                      // Merge: quelli già collegati + tutti quelli disponibili non ancora collegati
                      const merged = [
                        ...(entry.converted_contracts || []),
                        ...(entry.client_contracts || []).filter(c => !alreadyById.has(c.id)),
                      ];
                      if (merged.length === 0) {
                        return <p className="text-xs text-gray-400 py-2">Nessun contratto disponibile.</p>;
                      }
                      return (
                        <div className="border border-gray-200 rounded-md max-h-52 overflow-y-auto p-2 space-y-1">
                          {merged.map(c => {
                            const isSelected = editConvertedIds.includes(c.id);
                            const isOrphan = !availableById.has(c.id); // già collegato ma non nella lista corrente (altro cliente)
                            return (
                              <label key={c.id} className={`flex items-center gap-2 text-sm py-1.5 rounded px-2 ${canWrite ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-70'}`}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleEditConverted(c.id)}
                                  data-testid={`edit-converted-${c.id}`}
                                  disabled={!canWrite}
                                />
                                <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                                  <div className="min-w-0">
                                    <div className="truncate">{c.name}</div>
                                    {(isOrphan || c.client_name) && (
                                      <div className="text-[11px] text-gray-500 truncate">
                                        Cliente: {c.client_name || '—'}
                                        {isOrphan && <span className="ml-1 text-amber-600">(altra ragione sociale)</span>}
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">{fmtCur(c.value)} · {fmtDate(c.signing_date)}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Chiudi</Button>
              {canWrite && (
                <Button onClick={handleSave} disabled={saving} data-testid="save-entry-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva
                </Button>
              )}
            </DialogFooter>
          </div>
        )}

        {/* Win mode */}
        {mode === 'win' && (
          <div className="space-y-4 pt-2">
            <div>
              <Label className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Data esito (Vinto)</Label>
              <Input type="date" value={winData.outcome_date} onChange={(e) => setWinData({ ...winData, outcome_date: e.target.value })} data-testid="win-date-input" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Contratti convertiti (venduti dopo il Front-End)</Label>
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer" data-testid="show-all-clients-toggle-label">
                  <Checkbox
                    checked={showAllClientsInWin}
                    onCheckedChange={handleToggleAllClients}
                    data-testid="show-all-clients-toggle"
                  />
                  Mostra contratti di tutti i clienti
                </label>
              </div>
              <p className="text-xs text-gray-500 mb-1.5">
                Seleziona i contratti realmente venduti dopo l&apos;assessment. Il valore effettivo di conversione sarà la somma di questi.
                {showAllClientsInWin && (
                  <span className="text-amber-700"> Attenzione: vedi contratti di tutti i clienti (usa per clienti con più ragioni sociali).</span>
                )}
              </p>
              {(entry.client_contracts || []).length > 5 && (
                <div className="relative mb-2">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Cerca contratto o cliente…"
                    value={contractSearchQuery}
                    onChange={(e) => setContractSearchQuery(e.target.value)}
                    className="h-8 pl-8 pr-8 text-sm"
                    data-testid="win-contracts-search"
                  />
                  {contractSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setContractSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="win-contracts-search-clear"
                      aria-label="Cancella ricerca"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
              {(() => {
                const q = contractSearchQuery.trim().toLowerCase();
                const filtered = q
                  ? (entry.client_contracts || []).filter(c =>
                      (c.name || '').toLowerCase().includes(q)
                      || (c.client_name || '').toLowerCase().includes(q)
                    )
                  : (entry.client_contracts || []);
                const total = (entry.client_contracts || []).length;
                return (
                  <>
                    {q && (
                      <p className="text-[11px] text-gray-500 mb-1">
                        {filtered.length} risultati su {total}
                      </p>
                    )}
                    <div className="border border-gray-200 rounded-md max-h-52 overflow-y-auto p-2 space-y-1">
                      {filtered.length === 0 ? (
                        <p className="text-xs text-gray-400 py-3 text-center">
                          {q
                            ? 'Nessun contratto corrisponde alla ricerca.'
                            : showAllClientsInWin
                              ? 'Nessun contratto disponibile.'
                              : 'Nessun altro contratto trovato per questo cliente.'}
                        </p>
                      ) : filtered.map(c => (
                        <label key={c.id} className="flex items-center gap-2 text-sm py-1.5 hover:bg-gray-50 rounded px-2 cursor-pointer">
                          <Checkbox
                            checked={winData.converted_contract_ids.includes(c.id)}
                            onCheckedChange={() => toggleConvertedContract(c.id)}
                            data-testid={`converted-checkbox-${c.id}`}
                          />
                          <div className="flex-1 flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate">{c.name}</div>
                              {showAllClientsInWin && c.client_name && (
                                <div className="text-[11px] text-gray-500 truncate">Cliente: {c.client_name}</div>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">{fmtCur(c.value)} · {fmtDate(c.signing_date)}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                );
              })()}
              {winData.converted_contract_ids.length > 0 && (
                <div className="mt-2 px-3 py-2 bg-emerald-50 rounded-md text-sm">
                  Totale valore convertito: <strong>{fmtCur(convertedTotal)}</strong>
                  <span className="text-xs text-gray-500 ml-2">
                    (vs opportunità stimata {fmtCur(entry.opportunity_value)})
                  </span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode('edit')}>Annulla</Button>
              <Button onClick={handleWin} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-win-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <CheckCircle2 className="w-4 h-4 mr-2" />Conferma Vinto
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Lose mode */}
        {mode === 'lose' && (
          <div className="space-y-4 pt-2">
            <div>
              <Label className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Data esito (Perso)</Label>
              <Input type="date" value={loseData.outcome_date} onChange={(e) => setLoseData({ ...loseData, outcome_date: e.target.value })} data-testid="lose-date-input" />
            </div>
            <div>
              <Label>Motivazione (opzionale)</Label>
              <Textarea rows={3} value={loseData.outcome_reason} onChange={(e) => setLoseData({ ...loseData, outcome_reason: e.target.value })} placeholder="Es. Cliente ha scelto un competitor, budget insufficiente, ecc." data-testid="lose-reason-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMode('edit')}>Annulla</Button>
              <Button onClick={handleLose} disabled={saving} variant="destructive" data-testid="confirm-lose-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <XCircle className="w-4 h-4 mr-2" />Conferma Perso
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
